import { SettingsPermissions } from "@redotech/account-common/permissions";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { Navigate, useLocation } from "react-router-dom";
import { IntegrationsHubRedirect } from "../integrations-hub-redirect";

const WarehouseSettingsRedirect = () => {
  const { pathname, search } = useLocation();
  return (
    <Navigate
      replace
      to={`${pathname.replace(
        "/settings/order-management/returns-centers",
        "/settings/warehouse",
      )}${search}`}
    />
  );
};

export const fulfillmentSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "Order management",
      breadcrumbNavigate: false,
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "order-management",
    children: [
      {
        handle: {
          breadcrumb: "Order editing",
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "order-editing",
        lazy: async () => {
          const { OrderEditingSettingsPage } =
            await import("../../../../setting/order-editing/order-editing-settings");
          return { Component: OrderEditingSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Warehouse",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "returns-centers",
        children: [
          { path: "", element: <WarehouseSettingsRedirect /> },
          {
            handle: {
              breadcrumb: "Center",
              permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            },
            path: ":centerId",
            element: <WarehouseSettingsRedirect />,
          },
        ],
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Order management",
      breadcrumbNavigate: false,
      permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    },
    path: "oms",
    children: [
      {
        handle: {
          breadcrumb: "General",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "general",
        lazy: async () => {
          const { GeneralOmsSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/general");
          return { Component: GeneralOmsSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Auto-merge fulfillments",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "automerge-shipments",
        lazy: async () => {
          const { AutoMergeSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/auto-merge-shipments");
          return { Component: AutoMergeSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Automations",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "automations",
        children: [
          {
            index: true,
            lazy: async () => {
              const { AutomationsPage: Automations } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/automations/automations");
              return { Component: Automations };
            },
          },
          {
            handle: {
              breadcrumb: "Automation Builder",
              permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            },
            path: ":automationId",

            async lazy() {
              const { OmsAutomationBuilder } =
                await import("../../../../setting/oms/automations/automation-builder");
              return { Component: OmsAutomationBuilder };
            },
          },
        ],
      },
      {
        handle: {
          breadcrumb: "Custom imports",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "custom-import-schemas",
        lazy: async () => {
          const { CustomImportSchemas } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/custom-import-schemas");
          return { Component: CustomImportSchemas };
        },
      },
      {
        handle: {
          breadcrumb: "Integrations",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "integrations",
        element: <IntegrationsHubRedirect to="../../integrations" />,
      },
      {
        handle: {
          breadcrumb: "International",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "international",
        lazy: async () => {
          const { LoadInternationalSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/international");
          return { Component: LoadInternationalSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Packages",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "packages",
        lazy: async () => {
          const { ParcelsPage: Parcels } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/parcels");
          return { Component: Parcels };
        },
      },
      {
        handle: {
          breadcrumb: "Packing slips",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "printing/packing-slips",
        children: [
          {
            index: true,
            async lazy() {
              const { PackingSlipsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slips-page");
              return { Component: PackingSlipsPage };
            },
          },
          {
            handle: { breadcrumb: "Packing slip builder" },
            path: ":packingSlipId",
            async lazy() {
              const { PackingSlipBuilder } =
                await import("@redotech/redo-merchant-fulfillment-app/packing-slips/packing-slip-builder");
              return { Component: PackingSlipBuilder };
            },
          },
        ],
      },
      {
        path: "balance",
        element: <Navigate replace to="../../billing/outbound" />,
      },
      {
        handle: {
          breadcrumb: "Presets",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "presets",
        lazy: async () => {
          const { PresetsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/presets/presets");
          return { Component: PresetsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Printing",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "printing",
        lazy: async () => {
          const { PrintingSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/printing");
          return { Component: PrintingSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Reports",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "reports",
        lazy: async () => {
          const { ReportsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/reports/oms-reports-page");
          return { Component: ReportsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Scanning & warehouse",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "scanning-warehouse",
        lazy: async () => {
          const { ScanningWarehouseSettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/scanning-warehouse");
          return { Component: ScanningWarehouseSettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Security",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "security",
        lazy: async () => {
          const { SecuritySettingsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/security");
          return { Component: SecuritySettingsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Warehouse builder",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "warehouse-builder",
        lazy: async () => {
          const { WarehouseBuilderWithProvider } =
            await import("@redotech/redo-merchant-fulfillment-app/wms/warehouse-builder/warehouse-builder");
          return { Component: WarehouseBuilderWithProvider };
        },
      },
      {
        handle: {
          breadcrumb: "Warehouse layouts",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "warehouse-layouts",
        lazy: async () => {
          const { WarehouseLayoutsPage } =
            await import("@redotech/redo-merchant-fulfillment-app/setting/warehouse-layouts");
          return { Component: WarehouseLayoutsPage };
        },
      },
      {
        handle: {
          breadcrumb: "Work Order Flows",
          permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
        },
        path: "work-order-flows",
        children: [
          {
            index: true,
            lazy: async () => {
              const { WorkOrderFlowsPage } =
                await import("@redotech/redo-merchant-fulfillment-app/setting/work-order-flows/work-order-flows");
              return { Component: WorkOrderFlowsPage };
            },
          },
          {
            handle: {
              breadcrumb: "Flow Builder",
              permission: SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
            },
            path: ":flowId",
            async lazy() {
              const { WorkOrderFlowBuilder } =
                await import("../../../../setting/oms/work-order-flows/work-order-flow-builder");
              return { Component: WorkOrderFlowBuilder };
            },
          },
        ],
      },
    ],
  },
  { path: "parcels", element: <Navigate replace to="../oms/packages" /> },
  { path: "balance", element: <Navigate replace to="../oms/balance" /> },
  // We moved /locations -> /shipping, so we'll redirect users just in case.
  { path: "locations", element: <Navigate replace to="../shipping" /> },
];
